import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { getListingsById } from '../../../ducks/marketplaceData.duck';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink, AvatarLarge, H3 } from '../../../components';
import { userDisplayNameAsString } from '../../../util/data';
import { extractYouTubeID } from '../../../util/string';
import { getFieldValue } from '../../../util/fieldHelpers';
import { useConfiguration } from '../../../context/configurationContext';
import { YoutubeEmbed } from '../Primitives/YoutubeEmbed';

import css from './SectionAuthors.module.css';

const AuthorCard = ({ user, userType, targetUserType, onClick, userFieldConfig }) => {
  const profile = user.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const displayUserName = userDisplayNameAsString(user);

  // Extract user data fields - adjust these field keys based on your actual user field configuration
  const gender = getFieldValue(publicData, 'gender');
  const yearsOfExperience = getFieldValue(publicData, 'years_experience') || getFieldValue(publicData, 'experience');
  const location = getFieldValue(publicData, 'userLocation');
  const youtubeVideo = getFieldValue(publicData, 'introVideo');
  const communicationPreference = getFieldValue(publicData, 'communication_preference');
  const instagramHandle = getFieldValue(publicData, 'instagram');

  const youtubeId = youtubeVideo ? extractYouTubeID(youtubeVideo) : null;

  // Helper function to get field label from configuration
  const getFieldLabel = (fieldKey, value) => {
    if (!userFieldConfig || !value) return null;

    const fieldConfig = userFieldConfig.find(field => field.key === fieldKey);
    if (!fieldConfig) return value;

    // For enum fields, find the label for the selected option
    if (fieldConfig.schemaType === 'enum' && fieldConfig.enumOptions) {
      const selectedOption = fieldConfig.enumOptions.find(option => option.option === value);
      return selectedOption?.label || value;
    }

    // For other field types, return the value as is
    return value;
  };

  if (targetUserType && userType !== targetUserType) {
    return null;
  }

  return (
    <div className={css.authorCard} onClick={onClick}>
      <NamedLink
        name="ProfilePage"
        params={{ id: user.id.uuid }}
        className={css.authorLink}
      >
        <div className={css.authorHeader}>
          <AvatarLarge
            className={css.authorAvatar}
            user={user}
            disableProfileLink
          />
          <div className={css.authorInfo}>
            <H3 className={css.authorName}>
              {displayUserName}
            </H3>
            {location && (
              <div className={css.authorLocation}>
                📍 {getFieldLabel('userLocation', location) || location}
              </div>
            )}
          </div>
        </div>

        <div className={css.authorDetails}>
          {gender && (
            <div className={css.authorDetail}>
              <span className={css.detailLabel}>Gender:</span>
              <span className={css.detailValue}>{getFieldLabel('gender', gender) || gender}</span>
            </div>
          )}

          {yearsOfExperience && (
            <div className={css.authorDetail}>
              <span className={css.detailLabel}>Experience:</span>
              <span className={css.detailValue}>{getFieldLabel('years_experience', yearsOfExperience) || `${yearsOfExperience} years`}</span>
            </div>
          )}

          {communicationPreference && (
            <div className={css.authorDetail}>
              <span className={css.detailLabel}>Communication:</span>
              <span className={css.detailValue}>{getFieldLabel('communication_preference', communicationPreference) || communicationPreference}</span>
            </div>
          )}

          {instagramHandle && (
            <div className={css.authorDetail}>
              <span className={css.detailLabel}>Instagram:</span>
              <span className={css.detailValue}>
                <a
                  href={`https://instagram.com/${instagramHandle.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.instagramLink}
                >
                  @{instagramHandle}
                </a>
              </span>
            </div>
          )}
        </div>

        {youtubeId && (
          <div className={css.authorVideo}>
            <YoutubeEmbed
              youtubeVideoId={youtubeId}
              aspectRatio="16/9"
              className={css.videoEmbed}
            />
          </div>
        )}
      </NamedLink>
    </div>
  );
};

const SectionAuthorsComponent = ({ listings, section }) => {
  const config = useConfiguration();

  // Get target user type from section configuration (default to 'trainer')
  // Support multiple configuration paths for flexibility
  const targetUserType = section?.appearance?.targetUserType?.value ||
                         section?.targetUserType?.value ||
                         section?.publicData?.targetUserType ||
                         'trainer';

  // Get user field configuration for label resolution
  const userFieldConfig = config?.user?.userFields || [];

  // Group listings by author and filter by user type
  const authors = useMemo(() => {
    if (!listings || !Array.isArray(listings)) {
      return [];
    }

    // Create a map to group listings by author
    const authorMap = new Map();

    listings.forEach(listing => {
      const author = listing?.author;
      if (!author) return;

      const authorId = author.id.uuid;
      const userType = author.attributes?.profile?.publicData?.userType;

      // Filter for target user type
      if (targetUserType && userType !== targetUserType) {
        return;
      }

      if (!authorMap.has(authorId)) {
        authorMap.set(authorId, {
          user: author,
          userType,
          listings: []
        });
      }

      authorMap.get(authorId).listings.push(listing);
    });

    return Array.from(authorMap.values());
  }, [listings, targetUserType]);

  const totalAuthors = authors.length;

  return (
    <div className={css.sectionAuthors}>
      <div className={css.authorsSummary}>
        <H3 className={css.sectionTitle}>
          {section?.title?.value || 'Our Authors'}
        </H3>
        {section?.description?.value && (
          <p className={css.sectionDescription}>
            {section.description.value}
          </p>
        )}
        <div className={css.authorsCount}>
          <FormattedMessage
            id="SectionAuthors.foundAuthors"
            values={{ count: totalAuthors }}
          />
        </div>
      </div>

      <div className={css.authorsGrid}>
        {authors.map(({ user, userType }) => (
          <AuthorCard
            key={user.id.uuid}
            user={user}
            userType={userType}
            targetUserType={targetUserType}
            userFieldConfig={userFieldConfig}
          />
        ))}
      </div>

      {totalAuthors === 0 && (
        <div className={css.noAuthors}>
          <FormattedMessage id="SectionAuthors.noAuthorsFound" />
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const { searchResultsData, currentPageAssets } = state.hostedAssets || {};
  const currentPageResultIds = currentPageAssets
    .flatMap(pageID => searchResultsData?.[pageID]?.data ?? [])
    .map(l => l.id);
  const listings = getListingsById(state, currentPageResultIds);

  return { listings };
}

const SectionAuthors = compose(
  connect(mapStateToProps)
)(SectionAuthorsComponent);

export default SectionAuthors;
